/**
 * @documental
 * @export CheckboxGroup
 */
import { ReactNode } from "react";
import { forwardRef } from "react";

import { withSubComponents } from "../../utilities/withSubComponents";
import { InputBaseProps } from "../InputBaseProps";
import { RefComponent } from "../RefComponent";
import { Field } from "./Field";
import { Group } from "./Group";
import { Input } from "./Input";
import { Option } from "./Option";
import { Placeholder } from "./Placeholder";

/**
 * The `CheckboxGroup` component properties.
 */
export interface CheckboxGroupProps extends InputBaseProps<ReadonlyArray<string | number> | null> {
  /**
   * The options to display.
   */
  readonly children?: ReactNode;

  /**
   * Display options in a horizontal layout.
   * Value can be "vertical" or "horizontal".
   * Defaults to "vertical" if no value is supplied.
   */
  readonly orientation?: "vertical" | "horizontal";

  /**
   * Remove padding from the checkbox group.
   */
  readonly unpadded?: boolean;
}

/**
 * The `CheckboxGroup` component sub-components.
 */
export interface CheckboxGroupSubComponents {
  /**
   * Represents a single option which can be placed within the CheckboxGroup component.
   */
  readonly Option: typeof Option;

  /**
   * Represents a group of options which can be toggled by a master checkbox.
   */
  readonly Group: typeof Group;

  /**
   * A React Hook Form compatible CheckboxGroup component. For more information, see the documentation for
   * the <a href="https://react-hook-form.com/api">React Hook Form API</a>.
   */
  readonly Field: typeof Field;

  /**
   * A placeholder component for the CheckboxGroup which can be displayed while loading.
   */
  readonly Placeholder: typeof Placeholder;
}

/**
 * A form input that presents a group of series of checkboxes which can be toggled on and off.
 *
 * `import { CheckboxGroup } from "capitalrx-components-2.0/lib/forms";`
 *
 * <strong>To use the React Hook Form version add the following use the `Field` sub component:</strong>
 *
 * `<CheckboxGroup.Field .../>`
 *
 * <strong>To use the regular version of the TextArea</strong>
 *
 * `<CheckboxGroup .../>`
 *
 * The regular version acts like a normal field, in order to make it work
 * you need to pass in a `value` and an `onChange` for it to be interactive
 *
 * ```typescript
  const [value, onChange] = useSimpleInputState<string[] | null>(null);

  const handleChange = (newValue: string[] | null, prev: string[] | null) => {
    onChange(newValue, prev);
  };

  <CheckboxGroup value={value} label="Regular Checkbox Group" onChange={handleChange}>
    <CheckboxGroup.Option value="first">First Option</CheckboxGroup.Option>
    <CheckboxGroup.Option value="second">Second Option</CheckboxGroup.Option>
    <CheckboxGroup.Group label="Group">
      <CheckboxGroup.Option value="third">Third Option</CheckboxGroup.Option>
      <CheckboxGroup.Option value="fourth">Fourth Option</CheckboxGroup.Option>
    </CheckboxGroup.Group>
    <CheckboxGroup.Option value="fifth">Fifth Option</CheckboxGroup.Option>
  </CheckboxGroup>
  ```
 */
export const CheckboxGroup = withSubComponents<RefComponent<HTMLInputElement, CheckboxGroupProps>, CheckboxGroupSubComponents>(
  {
    Option,
    Field,
    Group,
    Placeholder,
  },
  forwardRef<HTMLInputElement, CheckboxGroupProps>(function CheckboxGroup(props, ref) {
    const { children } = props;

    return (
      <Input {...props} ref={ref}>
        {children}
      </Input>
    );
  }),
);
